// Utility functions for stock symbol validation and sanitization

export const validateStockSymbol = (symbol: string): boolean => {
  // Validation: 1-5 letters optionally followed by a dash or dot and 1 letter (e.g., BRK-B, BRK.B)
  const trimmed = symbol.trim();
  if (trimmed.length > 7) return false; // Max: 5 letters + dash/dot + 1 letter
  const symbolPattern = /^[A-Z]{1,5}([.-][A-Z])?$/;
  return symbolPattern.test(trimmed);
};

export const sanitizeStockSymbol = (symbol: string): string => {
  // Remove characters except letters, dashes, and periods, then convert to uppercase
  // Preserves class symbols like BRK-B, BRK.B
  return symbol
    .replace(/[^A-Za-z.-]/g, '')
    .toUpperCase()
    .slice(0, 7); // Max length: 5 letters + dash/dot + 1 letter (e.g., GOOGL-A)
};

export const sanitizeUserInput = (input: string): string => {
  // Basic sanitization for chat prompts - remove potentially harmful characters
  return input
    .replace(/[<>'"]/g, '') // Remove HTML/script injection chars
    .replace(/\{[^}]*\}/g, '') // Remove template injection attempts
    .replace(/\$\{[^}]*\}/g, '') // Remove template literal injection attempts
    .replace(/javascript:/gi, '') // Remove javascript protocol
    .replace(/data:/gi, '') // Remove data protocol
    .trim()
    .slice(0, 1000); // Limit length
};

export const sanitizeSymbolsForPrompt = (symbols: string[]): string[] => {
  // Sanitize and validate symbols for use in chat prompts
  return symbols
    .map((symbol) => sanitizeStockSymbol(symbol))
    .filter((symbol) => validateStockSymbol(symbol))
    .slice(0, 20); // Limit number of symbols to prevent prompt bloat
};

export const validateSymbolArray = (symbols: string[]): boolean => {
  // Validate that all symbols in array are valid stock symbols
  return symbols.length > 0 && symbols.every((symbol) => validateStockSymbol(symbol));
};

// Common abbreviations that should stay uppercase
const PRESERVE_UPPERCASE = ['ETF', 'LLC', 'INC', 'CORP', 'LP', 'LTD', 'PLC', 'AG', 'SA', 'NV', 'SE'];

export const toTitleCase = (str: string): string => {
  if (!str) return str ?? '';

  // Only convert if the entire string is all uppercase or all lowercase
  const isAllCaps = str === str.toUpperCase();
  const isAllLower = str === str.toLowerCase();
  if (!isAllCaps && !isAllLower) return str;

  return str
    .toLowerCase()
    .split(' ')
    .map((word) => {
      const upperWord = word.toUpperCase();
      if (PRESERVE_UPPERCASE.includes(upperWord)) return upperWord;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};
